import type {
  DirectoryOrganization,
  DirectoryUser,
  PagedPayload,
} from "@noverik/contracts";
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { apiRequest, ApiRequestError } from "../lib/api";

export function CustomersPage() {
  const { session } = useAuth();
  const [data, setData] = useState<PagedPayload<DirectoryUser> | null>(null);
  const [organizations, setOrganizations] = useState<DirectoryOrganization[]>([]);
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [createAccess, setCreateAccess] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [editingUser, setEditingUser] = useState<DirectoryUser | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editOrganizationId, setEditOrganizationId] = useState("");
  const [accountUser, setAccountUser] = useState<DirectoryUser | null>(null);
  const [accountPassword, setAccountPassword] = useState("");
  const [accountPasswordConfirmation, setAccountPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);
  const [resettingUserId, setResettingUserId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ page: String(page), limit: "25" });
    if (query) params.set("q", query);

    try {
      const next = await apiRequest<PagedPayload<DirectoryUser>>(
        "/users?" + params.toString(),
      );
      setData(next);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Não foi possível carregar os clientes.");
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    apiRequest<PagedPayload<DirectoryOrganization>>("/organizations?page=1&limit=100")
      .then((payload) => setOrganizations(payload.items))
      .catch(() => setOrganizations([]));
  }, []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setQuery(input.trim());
  };

  const resetCreate = () => {
    setName("");
    setEmail("");
    setOrganizationId("");
    setCreateAccess(false);
    setNewPassword("");
    setNewPasswordConfirmation("");
  };

  const createUser = async (event: FormEvent) => {
    event.preventDefault();

    if (!session?.csrfToken) return;

    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      const created = await apiRequest<DirectoryUser>("/users", {
        method: "POST",
        csrfToken: session.csrfToken,
        body: {
          name,
          email,
          organizationId: organizationId ? Number(organizationId) : null,
          password: createAccess ? newPassword : null,
          passwordConfirmation: createAccess ? newPasswordConfirmation : null,
        },
      });
      resetCreate();
      setShowCreate(false);
      setPage(1);
      setNotice(
        created.hasAccount
          ? "Cliente cadastrado com acesso ativo ao portal."
          : "Cliente cadastrado no diretório.",
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Não foi possível cadastrar o cliente.");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (user: DirectoryUser) => {
    setEditingUser(user);
    setEditName(user.name);
    setEditEmail(user.email);
    setEditOrganizationId(user.organization ? String(user.organization.id) : "");
    setAccountUser(null);
    setShowCreate(false);
    setError(null);
    setNotice(null);
  };

  const updateUser = async (event: FormEvent) => {
    event.preventDefault();

    if (!session?.csrfToken || !editingUser) return;

    setEditSaving(true);
    setError(null);
    setNotice(null);

    try {
      const updated = await apiRequest<DirectoryUser>(`/users/${editingUser.id}`, {
        method: "PUT",
        csrfToken: session.csrfToken,
        body: {
          name: editName,
          email: editEmail,
          organizationId: editOrganizationId ? Number(editOrganizationId) : null,
        },
      });
      setEditingUser(null);
      setNotice(`Cliente ${updated.name} atualizado com sucesso.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Não foi possível atualizar o cliente.");
    } finally {
      setEditSaving(false);
    }
  };

  const openAccount = (user: DirectoryUser) => {
    setEditingUser(null);
    setShowCreate(false);
    setAccountUser(user);
    setAccountPassword("");
    setAccountPasswordConfirmation("");
    setError(null);
    setNotice(null);
  };

  const createAccount = async (event: FormEvent) => {
    event.preventDefault();

    if (!session?.csrfToken || !accountUser) return;

    setAccountSaving(true);
    setError(null);
    setNotice(null);

    try {
      await apiRequest<DirectoryUser>(`/users/${accountUser.id}/account`, {
        method: "POST",
        csrfToken: session.csrfToken,
        body: {
          password: accountPassword,
          passwordConfirmation: accountPasswordConfirmation,
        },
      });
      setNotice(`Acesso ao portal criado para ${accountUser.name}.`);
      setAccountUser(null);
      setAccountPassword("");
      setAccountPasswordConfirmation("");
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Não foi possível criar o acesso.");
    } finally {
      setAccountSaving(false);
    }
  };

  const sendPasswordReset = async (user: DirectoryUser) => {
    if (!session?.csrfToken) return;

    setResettingUserId(user.id);
    setError(null);
    setNotice(null);

    try {
      await apiRequest<{ sent: boolean; email: string }>(
        `/users/${user.id}/password-reset-email`,
        {
          method: "POST",
          csrfToken: session.csrfToken,
        },
      );
      setNotice(`E-mail de recuperação enviado para ${user.email}.`);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Não foi possível enviar a recuperação de senha.",
      );
    } finally {
      setResettingUserId(null);
    }
  };

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">RELACIONAMENTO</span>
          <h1>Clientes</h1>
          <p>Cadastre clientes, vincule organizações e gerencie o acesso ao portal.</p>
        </div>
        <button
          className="button primary"
          onClick={() => {
            setEditingUser(null);
            setAccountUser(null);
            setShowCreate((value) => !value);
          }}
        >
          <UserPlus size={17} /> Novo cliente
        </button>
      </div>

      {showCreate && (
        <form className="inline-create-card" onSubmit={createUser}>
          <div className="inline-create-heading">
            <span className="meta-icon blue"><Plus size={18} /></span>
            <div>
              <strong>Cadastrar cliente</strong>
              <span>O cadastro será criado diretamente no diretório do osTicket.</span>
            </div>
          </div>

          <div className="inline-create-grid user">
            <label className="field">
              <span>Nome</span>
              <input value={name} onChange={(event) => setName(event.target.value)} required />
            </label>
            <label className="field">
              <span>E-mail</span>
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </label>
            <label className="field">
              <span>Organização</span>
              <select value={organizationId} onChange={(event) => setOrganizationId(event.target.value)}>
                <option value="">Sem organização</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </label>
            <button className="button primary" disabled={saving}>
              {saving ? "Salvando..." : "Cadastrar"}
            </button>
          </div>

          <label className="portal-access-toggle">
            <input
              type="checkbox"
              checked={createAccess}
              onChange={(event) => setCreateAccess(event.target.checked)}
            />
            <span>
              <strong>Criar acesso ao portal</strong>
              <small>O cliente poderá entrar com o e-mail e a senha temporária informada abaixo.</small>
            </span>
          </label>

          {createAccess && (
            <div className="account-password-grid">
              <label className="field">
                <span>Senha temporária</span>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>
              <label className="field">
                <span>Confirmar senha</span>
                <input
                  type="password"
                  value={newPasswordConfirmation}
                  onChange={(event) => setNewPasswordConfirmation(event.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>
            </div>
          )}
        </form>
      )}

      {editingUser && (
        <form className="inline-create-card" onSubmit={updateUser}>
          <div className="inline-create-heading">
            <span className="meta-icon blue"><Pencil size={18} /></span>
            <div>
              <strong>Editar cliente</strong>
              <span>Atualize os dados do cliente diretamente no diretório do osTicket.</span>
            </div>
          </div>

          <div className="inline-create-grid user customer-edit-grid">
            <label className="field">
              <span>Nome</span>
              <input value={editName} onChange={(event) => setEditName(event.target.value)} required />
            </label>
            <label className="field">
              <span>E-mail</span>
              <input type="email" value={editEmail} onChange={(event) => setEditEmail(event.target.value)} required />
            </label>
            <label className="field">
              <span>Organização</span>
              <select value={editOrganizationId} onChange={(event) => setEditOrganizationId(event.target.value)}>
                <option value="">Sem organização</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </label>
            <div className="customer-edit-actions">
              <button type="button" className="button secondary" onClick={() => setEditingUser(null)}>
                Cancelar
              </button>
              <button className="button primary" disabled={editSaving}>
                {editSaving ? "Salvando..." : "Salvar alterações"}
              </button>
            </div>
          </div>
        </form>
      )}

      {accountUser && (
        <form className="inline-create-card account-create-card" onSubmit={createAccount}>
          <div className="inline-create-heading">
            <span className="meta-icon blue"><KeyRound size={18} /></span>
            <div>
              <strong>Criar acesso para {accountUser.name}</strong>
              <span>{accountUser.email}{accountUser.organization ? ` · ${accountUser.organization.name}` : ""}</span>
            </div>
          </div>

          <div className="account-password-grid with-actions">
            <label className="field">
              <span>Senha temporária</span>
              <input
                type="password"
                value={accountPassword}
                onChange={(event) => setAccountPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <label className="field">
              <span>Confirmar senha</span>
              <input
                type="password"
                value={accountPasswordConfirmation}
                onChange={(event) => setAccountPasswordConfirmation(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <div className="account-create-actions">
              <button type="button" className="button secondary" onClick={() => setAccountUser(null)}>
                Cancelar
              </button>
              <button className="button primary" disabled={accountSaving}>
                <ShieldCheck size={16} /> {accountSaving ? "Criando..." : "Criar acesso"}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="ticket-controls">
        <form className="searchbox inline" onSubmit={submitSearch}>
          <Search size={17} />
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Buscar por nome, e-mail, telefone ou organização..."
          />
          <button type="submit" className="search-submit">Buscar</button>
        </form>
      </div>

      {notice && <div className="data-message">{notice}</div>}
      {error && <div className="data-message error">{error}</div>}

      {!error && (
        <div className="ticket-table-wrap">
          <table className="ticket-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Organização</th>
                <th>Conta</th>
                <th>Chamados</th>
                <th>Atualização</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6}><div className="table-loading">Carregando clientes...</div></td></tr>}
              {!loading && data?.items.length === 0 && (
                <tr><td colSpan={6}><div className="table-empty"><strong>Nenhum cliente encontrado.</strong></div></td></tr>
              )}
              {!loading && data?.items.map((user) => (
                <tr key={user.id}>
                  <td>
                    <span className="requester-name">{user.name}</span>
                    <small>{user.email}</small>
                  </td>
                  <td>{user.organization ? <span className="org-cell"><Building2 size={14} /> {user.organization.name}</span> : "—"}</td>
                  <td><span className={user.hasAccount ? "status status-open" : "status status-closed"}>{user.hasAccount ? "Ativa" : "Sem acesso"}</span></td>
                  <td>{user.ticketCount}</td>
                  <td>{user.updatedAt ? new Date(user.updatedAt).toLocaleString("pt-BR") : "—"}</td>
                  <td>
                    <div className="customer-row-actions">
                      <button className="button secondary compact" onClick={() => openEdit(user)}>
                        <Pencil size={14} /> Editar
                      </button>
                      {!user.hasAccount ? (
                        <button className="button secondary compact" onClick={() => openAccount(user)}>
                          <KeyRound size={14} /> Criar acesso
                        </button>
                      ) : (
                        <button
                          className="button secondary compact"
                          disabled={resettingUserId === user.id}
                          onClick={() => void sendPasswordReset(user)}
                        >
                          <KeyRound size={14} />
                          {resettingUserId === user.id ? "Enviando..." : "Enviar recuperação"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data && data.pages > 1 && (
        <div className="pagination">
          <span>Página {data.page} de {data.pages}</span>
          <div>
            <button className="button secondary" disabled={data.page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
              <ChevronLeft size={16} /> Anterior
            </button>
            <button className="button secondary" disabled={data.page >= data.pages} onClick={() => setPage((value) => value + 1)}>
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
